import apiService from 'library/api-service';
import { useEffect, useRef, useState } from 'react';
import type { ActivityBoardPermissionItem } from 'library/types';

export type PermissionActivityCacheStatus = 'idle' | 'loading' | 'ready' | 'error';
export type PermissionActivityScope = 'admin' | 'user';

type PermissionActivityCacheState = {
  items: ActivityBoardPermissionItem[];
  scope: PermissionActivityScope | null;
  status: PermissionActivityCacheStatus;
};

const deduplicatePermissionActivities = (
  permissionActivities: readonly ActivityBoardPermissionItem[]
): ActivityBoardPermissionItem[] => {
  const uniqueItems = new Map<string, ActivityBoardPermissionItem>();

  permissionActivities.forEach((item) => {
    const itemKey = [item.assignedUserId, item.id, item.activityTypeId, item.startDate, item.endDate].join(':');
    uniqueItems.set(itemKey, item);
  });

  return Array.from(uniqueItems.values());
};

/**
 * Keeps the unfiltered permission response stable for the lifetime of this page.
 * Date, user and tab changes only derive data from this local cache and never
 * trigger another request.
 */
export const usePermissionActivityCache = (scope: PermissionActivityScope | null) => {
  const requestsRef = useRef(
    new Map<PermissionActivityScope, ReturnType<typeof apiService.getActivityBoardPermissions>>()
  );
  const [state, setState] = useState<PermissionActivityCacheState>({
    items: [],
    scope: null,
    status: 'idle',
  });

  useEffect(() => {
    if (!scope) return;

    let request = requestsRef.current.get(scope);
    if (!request) {
      request = apiService.getActivityBoardPermissions();
      requestsRef.current.set(scope, request);
    }

    let active = true;
    setState({ items: [], scope, status: 'loading' });

    request
      .then((response) => {
        if (!active) return;

        if (response.isSuccess) {
          setState({
            items: deduplicatePermissionActivities(response.data || []),
            scope,
            status: 'ready',
          });
          return;
        }

        setState({ items: [], scope, status: 'error' });
      })
      .catch(() => {
        if (active) setState({ items: [], scope, status: 'error' });
      });

    return () => {
      active = false;
    };
  }, [scope]);

  if (!scope) return { items: [], status: 'idle' as const };
  if (state.scope !== scope) return { items: [], status: 'loading' as const };

  return { items: state.items, status: state.status } as const;
};
